import { useNode } from "@craftjs/core";
import { isUpdatedFromDefault } from "../../utils";
import React, { useCallback, useState } from "react";

import {
  PencilActiveIcon,
  PaintIcon,
  HorizontalTableIcon,
  VerticalTableIcon,
  SettingsIcon,
} from "../../../assets/ui-icons";
import NumberInput from "../../ui-kit/number";
import ToggleInput from "../../ui-kit/toggle";

import {
  ColorInput,
  FormatOptionsInput,
  Panel,
  SpacerInput,
  Tabs,
} from "./../../ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import RightBar from "../../ui-kit/rightbar";
import { throttle, debounce } from "lodash";
import { IconPack } from "../../../assets/icon-pack";
import { INPUT_LIMITS } from "../../input-limits";

export const CMSTableSettings = () => {
  const {
    actions: { setProp },
    props,
    id,
  } = useNode((node) => ({
    text: node.data.props.text,
    props: node.data.props,
  }));

  const [isActive, setIsActive] = useState("general");

  const {
    table,
    tableHeader,
    tableFooter,
    equalColumnsWidth,
    textColor,
    backgroundColor,
  } = props;

  const [internalRows, setInternalRows] = React.useState(table?.length ?? 0);
  const [internalCols, setInternalCols] = React.useState(
    table?.[0]?.length ?? 0,
  );

  // Throttle rows and column change
  const onRowsChange = (val) => {
    var currVal = Number(val);
    const diffRows = currVal - (table?.length ?? 0);
    if (currVal < (table?.length ?? 1)) {
      // deleting row
      setProp((props) => {
        var temp = [...(table ? table : [])]; // 2d
        temp.splice(-1, 1);
        props.table = temp;
        return props;
      });
    } else {
      // adding row
      setProp((props) => {
        var temp = [...(table ? table : [])]; // 2d
        for (let i = 0; i < diffRows; i++) {
          temp.push(new Array(table?.[0].length ?? 1).fill(""));
        }
        props.table = temp;
        return props;
      });
    }
  };

  const onColsChange = (val) => {
    var currVal = Number(val);
    const diffCols = currVal - (table?.[0].length ?? 1);
    setProp((props) => {
      // deleting cols
      if (currVal < (table?.[0].length ?? 1)) {
        var newArr = [...(table ? table : [])].map((subArr) => {
          return subArr.slice(0, diffCols);
        });
        if (tableHeader) {
          var tempHeader = [...tableHeader];
          tempHeader.splice(-1, 1);
          props.tableHeader = tempHeader;
        }
        if (tableFooter) {
          var tempFooter = [...tableFooter];
          tempFooter.splice(-1, 1);
          props.tableFooter = tempFooter;
        }
        props.table = newArr;

        return props;
      } else {
        // adding cols
        var newArr = [...table].map((row) => {
          return [...row, ...new Array(diffCols).fill("")];
        });
        props.table = newArr;
        // create dynamic header and footer
        if (tableHeader && tableHeader.length > 0) {
          var tempHeader = [...tableHeader, ""];
          props.tableHeader = tempHeader;
        }
        if (tableFooter && tableFooter.length > 0) {
          var tempFooter = [...tableFooter, ""];
          props.tableFooter = tempFooter;
        }
        return props;
      }
    });
  };

  // Debounce onRowsChange and onColsChange. Making sure that the table is added as dependency
  const onRowsChangeThrottle = useCallback(
    debounce((val) => onRowsChange(val), 300),
    [table],
  );

  const onColsChangeThrottle = useCallback(
    debounce((val) => onColsChange(val), 300),
    [table, tableHeader, tableFooter],
  );

  // Auto update internalRows and internalCols
  React.useEffect(() => {
    setInternalRows(table?.length ?? 0);
    setInternalCols(table?.[0]?.length ?? 0);
  }, [table]);

  const General = () => {
    return (
      <>
        <SpacerInput>
          <NumberInput
            icon={<HorizontalTableIcon />}
            value={internalRows}
            onChange={(val) => {
              setInternalRows(val);
              onRowsChangeThrottle(val);
            }}
            // min={1}
            // max={100}
            // step={1}

            min={INPUT_LIMITS["table-rows"].minlength}
            max={INPUT_LIMITS["table-rows"].maxlength}
            step={INPUT_LIMITS["table-rows"].step}
            label={"Rows"}
          />
          <NumberInput
            icon={<VerticalTableIcon />}
            value={internalCols}
            onChange={(val) => {
              setInternalCols(val);
              onColsChangeThrottle(val);
            }}
            // min={1}
            // max={15}
            // step={1}
            min={INPUT_LIMITS["table-cols"].minlength}
            max={INPUT_LIMITS["table-cols"].maxlength}
            step={INPUT_LIMITS["table-cols"].step}
            label={"Column"}
          />
          <FormatOptionsInput label={"Font Style"} />
        </SpacerInput>
      </>
    );
  };

  const Styles = () => {
    return (
      <div className="w-full rounded-[21px] py-2 bg-white flex flex-col">
        <Panel label={"Colors"} isOpen={true}>
          <SpacerInput>
            <ColorInput
              value={textColor}
              onChange={(val) => {
                setProp((props) => {
                  props.textColor = val;
                });
              }}
              label={"Text"}
              useTextColorPalette={true}
              supportSelection={true}
            />
            <ColorInput
              value={backgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.backgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </SpacerInput>
        </Panel>
      </div>
    );
  };

  const Advance = () => {
    return (
      <div className="w-full rounded-[21px] py-2 bg-white flex flex-col">
        <SpacerInput>
          <ToggleInput
            value={tableHeader && tableHeader.length > 0}
            onChange={(val) => {
              if (val) {
                setProp((props) => {
                  props.tableHeader = Array(table[0].length).fill("");
                  return props;
                });
              } else {
                setProp((props) => {
                  props.tableHeader = undefined;
                });
              }
            }}
            label={"Header"}
          />
          <ToggleInput
            value={equalColumnsWidth}
            onChange={(val) => {
              setProp((props) => {
                props.equalColumnsWidth = val;
              });
            }}
            label={"Equal Column Width"}
          />
        </SpacerInput>
      </div>
    );
  };

  const tabs = [
    {
      label: "General",
      key: "general",
      icon: IconPack.GeneralSettings,
      children: General(),
      isUpdated: isUpdatedFromDefault([]),
    },
    {
      label: "Style",
      key: "style",
      icon: IconPack.Style,
      children: Styles(),
      isUpdated: isUpdatedFromDefault([]),
    },
    {
      label: "Advanced",
      key: "advanced",
      icon: IconPack.Settings,
      children: Advance(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div className="h-full w-[330px] post-edit-right-sidebar cursor-default p-2 overflow-auto dashboard-ui-scrollbar ">
      <div className="w-full rounded-t-[21px] bg-white">
        <div className="p-5 flex justify-between">
          <RightBar
            title={"Table"}
            icon={<FontAwesomeIcon icon={faTable} color="#8402E9" size="2xl" />}
          />
        </div>

        <Tabs id={`block-cms-settings-tab-${id}`} items={tabs} />

        <div className="py-2 bg-ui-light-white"></div>
      </div>
    </div>
  );
};
